import Message from "@/models/conversation/Message";
import { HourlyStats } from "@/models/statistic/Statistic";
import type { DateRange } from "@capacit-com/lara-law-shared-ui";
const getRatingValue = (rating: string | null | undefined) => {
  switch (rating) {
    case "Upvote":
      return 1;
    case "Downvote":
      return -1;
    case "Neutral":
      return 0;
    default:
      return 0;
  }
};
export const getMessageRatings = (messages: Message[]) => {
  if (!messages.length) return { average: 0, total: 0 };

  const validRatings = messages.filter(
    (msg) => msg.rating !== undefined && msg.rating !== null
  );
  if (!validRatings.length) return { average: 0, total: 0 };

  const total = validRatings.reduce<number>(
    (sum, msg) => sum + getRatingValue(msg.rating),
    0
  );
  return {
    average: total / validRatings.length,
    total: validRatings.length,
  };
};

export const filterMessagesByDateRange = (
  messages: Message[],
  dateRange?: { from: Date; to: Date }
) => {
  if (!dateRange) return messages;

  return messages.filter((message) => {
    const messageDate = new Date(message.createdAt);
    return messageDate >= dateRange.from && messageDate <= dateRange.to;
  });
};

const aggregateRatingsByInterval = (
  data: HourlyStats[],
  interval: "day" | "month",
  startDate: Date,
  endDate: Date
): HourlyStats[] => {
  const result: HourlyStats[] = [];
  const dateMap = new Map<string, HourlyStats[]>();
  // Create base structure with all possible intervals
  const current = new Date(startDate);
  while (current <= endDate) {
    const key =
      interval === "day"
        ? current.toISOString().split("T")[0]
        : `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(
            2,
            "0"
          )}`;

    dateMap.set(key, []);

    if (interval === "day") {
      current.setDate(current.getDate() + 1);
    } else {
      current.setMonth(current.getMonth() + 1);
    }
  }

  // Group existing data into intervals
  data.forEach((item) => {
    const date = new Date(item.hour);
    const key =
      interval === "day"
        ? date.toISOString().split("T")[0]
        : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
            2,
            "0"
          )}`;

    if (dateMap.has(key)) {
      dateMap.get(key)?.push(item);
    }
  });

  // Process each interval - CHANGED FROM AVERAGE TO SUM
  dateMap.forEach((items, key) => {
    const total = items.reduce(
      (sum, item) => sum + (item.nonWordMessageCount || 0),
      0
    );

    result.push({
      hour:
        interval === "day" ? `${key}T00:00:00.000Z` : `${key}-01T00:00:00.000Z`,
      nonWordMessageCount: total, // Changed from average to total sum
      wordMessageCount: undefined,
      nonWordConversationCount: undefined,
      wordConversationCount: undefined,
    });
  });

  return result.sort(
    (a, b) => new Date(a.hour).getTime() - new Date(b.hour).getTime()
  );
};

export const createHourlyRatingGraph = (
  messages: Message[],
  dateRange?: DateRange
): HourlyStats[] => {
  const start =
    dateRange?.from ||
    new Date(Math.min(...messages.map((m) => new Date(m.createdAt).getTime())));
  const end =
    dateRange?.to ||
    new Date(Math.max(...messages.map((m) => new Date(m.createdAt).getTime())));

  // Create map of hourly ratings
  const hourlyMap = new Map<string, { sum: number; count: number }>();

  // Round dates to hours and set to start of day/end of day
  const startDate = new Date(start);
  startDate.setMinutes(0, 0, 0);
  const endDate = new Date(end);
  endDate.setHours(23, 59, 59, 999);
  // Populate all hours with zero values
  let current = startDate;
  while (current <= endDate) {
    const key = current.toISOString();
    hourlyMap.set(key, { sum: 0, count: 0 });
    current = new Date(current.setHours(current.getHours() + 1));
  }

  // Aggregate message ratings by hour
  messages.forEach((message) => {
    if (message.rating !== undefined && message.rating !== null) {
      const messageDate = new Date(message.createdAt);
      if (messageDate >= startDate && messageDate <= endDate) {
        const date = new Date(messageDate);
        date.setMinutes(0, 0, 0);
        const key = date.toISOString();

        const existing = hourlyMap.get(key) || { sum: 0, count: 0 };
        hourlyMap.set(key, {
          sum: existing.sum + getRatingValue(message.rating),
          count: existing.count + 1,
        });
      }
    }
  });

  // Convert map to array and prepare base data
  const hourlyRatings = Array.from(hourlyMap.entries())
    .map(([hour, data]) => ({
      hour,
      nonWordMessageCount: data.count,
      wordMessageCount: undefined,
      nonWordConversationCount: undefined,
      wordConversationCount: undefined,
    }))
    .sort((a, b) => new Date(a.hour).getTime() - new Date(b.hour).getTime());

  const diffDays = Math.ceil(
    (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (diffDays <= 3) {
    return hourlyRatings; // Hourly data
  } else if (diffDays <= 30) {
    return aggregateRatingsByInterval(hourlyRatings, "day", startDate, endDate);
  } else {
    return aggregateRatingsByInterval(
      hourlyRatings,
      "month",
      startDate,
      endDate
    );
  }
};
